// pages/reports/PurchaseInvoiceReport.jsx
import React, { useState, useEffect } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { setToogleHeader } from '../../core/redux/action';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import dayjs from 'dayjs';
import Select from 'react-select';
import { DatePicker } from 'antd';
import Table from '../../core/pagination/datatable';
import {
    ChevronUp,
    RotateCcw,
    Sliders,
    StopCircle,
    User,
    Calendar,
    Filter,
    Search,

    CheckCircle,
    XCircle,
    AlertTriangle,
    Package,
    Clock,
    CreditCard,
    PieChart,

    DollarSign
} from 'feather-icons-react/build/IconComponents';
import { purchaseActions } from '../../core/action/purchaseActions';
import { supplierActions } from '../../core/action/supplierAction';

const { RangePicker } = DatePicker;
const MySwal = withReactContent(Swal);

const PurchaseInvoiceReport = () => {
    const dispatch = useDispatch();
    const data = useSelector((state) => state.toggle_header);

    // State
    const [purchases, setPurchases] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isFilterVisible, setIsFilterVisible] = useState(false);
    const [reportType, setReportType] = useState('summary');

    // Dropdown options
    const [suppliers, setSuppliers] = useState([]);

    // Pagination
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 1
    });

    // Filters
    const [filters, setFilters] = useState({
        search: '',
        startDate: dayjs().subtract(30, 'day'),
        endDate: dayjs(),
        supplier_id: 0,
        status: '',
        payment_status: '',
        groupBy: 'daily'
    });

    // Summary statistics
    const [summary, setSummary] = useState({
        totalPurchases: 0,
        totalAmount: 0,
        totalPaid: 0,
        totalDue: 0,
        totalTax: 0,
        averagePurchase: 0,
        pendingCount: 0,
        paidCount: 0,
        partialCount: 0,
        cancelledCount: 0,
        receivedCount: 0
    });

    // Report type options
    const reportTypeOptions = [
        { value: 'summary', label: 'Summary Report' },
        { value: 'detailed', label: 'Detailed Report' },
        { value: 'payment', label: 'Payment Report' },
        { value: 'tax', label: 'Tax Report' },
        { value: 'supplier', label: 'Supplier-wise Report' },
        { value: 'item', label: 'Item-wise Report' }
    ];

    // Group by options
    const groupByOptions = [
        { value: 'daily', label: 'Daily' },
        { value: 'weekly', label: 'Weekly' },
        { value: 'monthly', label: 'Monthly' },
        { value: 'yearly', label: 'Yearly' }
    ];

    // Status filter options
    const statusFilterOptions = [
        { value: '', label: 'All Status' },
        { value: 'pending', label: 'Pending' },
        { value: 'approved', label: 'Approved' },
        { value: 'received', label: 'Received' },
        { value: 'cancelled', label: 'Cancelled' }
    ];

    // Payment status filter options
    const paymentStatusFilterOptions = [
        { value: '', label: 'All Payment' },
        { value: 'paid', label: 'Paid' },
        { value: 'partial', label: 'Partial' },
        { value: 'unpaid', label: 'Unpaid' }
    ];

    // Date range presets
    const datePresets = {
        'Today': [dayjs(), dayjs()],
        'Yesterday': [dayjs().subtract(1, 'day'), dayjs().subtract(1, 'day')],
        'Last 7 Days': [dayjs().subtract(7, 'day'), dayjs()],
        'Last 30 Days': [dayjs().subtract(30, 'day'), dayjs()],
        'This Month': [dayjs().startOf('month'), dayjs().endOf('month')],
        'Last Month': [dayjs().subtract(1, 'month').startOf('month'), dayjs().subtract(1, 'month').endOf('month')],
        'This Quarter': [dayjs().startOf('quarter'), dayjs().endOf('quarter')],
        'Last Quarter': [dayjs().subtract(1, 'quarter').startOf('quarter'), dayjs().subtract(1, 'quarter').endOf('quarter')],
        'This Year': [dayjs().startOf('year'), dayjs().endOf('year')],
        'Last Year': [dayjs().subtract(1, 'year').startOf('year'), dayjs().subtract(1, 'year').endOf('year')]
    };

    // Load initial data
    useEffect(() => {
        loadSuppliers();
        loadReportData();
    }, []);

    // Load suppliers
    const loadSuppliers = async () => {
        try {
            const result = await supplierActions.getSuppliers({ limit: 1000 });
            if (result.success) {
                setSuppliers([
                    { value: 0, label: 'All Suppliers' },
                    ...result.data.map(s => ({
                        value: s.id,
                        label: `${s.name} (${s.phone || 'No phone'})`
                    }))
                ]);
            }
        } catch (error) {
            console.error('Error loading suppliers:', error);
        }
    };

    // Load report data
    const loadReportData = async (page = pagination.page) => {
        try {
            setLoading(true);

            const params = {
                page,
                limit: pagination.limit,
                search: filters.search,
                start_date: filters.startDate ? filters.startDate.format('YYYY-MM-DD') : undefined,
                end_date: filters.endDate ? filters.endDate.format('YYYY-MM-DD') : undefined,
                supplier_id: filters.supplier_id || undefined,
                status: filters.status || undefined,
                payment_status: filters.payment_status || undefined,
                group_by: filters.groupBy,
                report_type: reportType
            };

            const result = await purchaseActions.getPurchases(params);

            if (result.success) {
                setPurchases(result.data || []);
                setPagination({
                    page: result.page || page,
                    limit: pagination.limit,
                    total: result.total || 0,
                    totalPages: result.totalPages || 1
                });

                // Update summary statistics
                calculateSummary(result.data || []);
            } else {
                MySwal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: result.message || 'Failed to load report data',
                    timer: 2000
                });
            }
        } catch (error) {
            console.error('Error loading report data:', error);
            MySwal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to load report data',
                timer: 2000
            });
        } finally {
            setLoading(false);
        }
    };

    // Calculate summary from purchases data
    const calculateSummary = (data) => {
        const totalPurchases = data.length;
        const totalAmount = data.reduce((sum, p) => sum + (p.grand_total || 0), 0);
        const totalPaid = data.reduce((sum, p) => sum + (p.paid_amount || 0), 0);
        const totalDue = data.reduce((sum, p) => sum + (p.due_amount || 0), 0);
        const totalTax = data.reduce((sum, p) => sum + (p.tax || 0), 0);

        const pendingCount = data.filter(p => p.status === 'pending').length;
        const receivedCount = data.filter(p => p.status === 'received' || p.status === 'approved').length;
        const paidCount = data.filter(p => p.payment_status === 'paid').length;
        const partialCount = data.filter(p => p.payment_status === 'partial').length;
        const cancelledCount = data.filter(p => p.status === 'cancelled').length;

        setSummary({
            totalPurchases,
            totalAmount,
            totalPaid,
            totalDue,
            totalTax,
            averagePurchase: totalPurchases > 0 ? totalAmount / totalPurchases : 0,
            pendingCount,
            receivedCount,
            paidCount,
            partialCount,
            cancelledCount
        });
    };

    // Handle filter toggle
    const toggleFilterVisibility = () => {
        setIsFilterVisible((prev) => !prev);
    };

    // Handle search
    const handleSearch = () => {
        setPagination(prev => ({ ...prev, page: 1 }));
        loadReportData(1);
    };

    // Reset filters
    const resetFilters = () => {
        setFilters({
            search: '',
            startDate: dayjs().subtract(30, 'day'),
            endDate: dayjs(),
            supplier_id: 0,
            status: '',
            payment_status: '',
            groupBy: 'daily'
        });
        setReportType('summary');
        setPagination(prev => ({ ...prev, page: 1 }));
        loadReportData(1);
    };

    // Handle date range change
    const handleDateRangeChange = (dates) => {
        if (dates && dates.length === 2) {
            setFilters(prev => ({
                ...prev,
                startDate: dates[0],
                endDate: dates[1]
            }));
        }
    };

    // Handle filter change
    const handleFilterChange = (filterName, value) => {
        setFilters(prev => ({ ...prev, [filterName]: value }));
    };

    // Handle report type change
    const handleReportTypeChange = (option) => {
        setReportType(option?.value || 'summary');
        setPagination(prev => ({ ...prev, page: 1 }));
        setTimeout(() => loadReportData(1), 0);
    };

    // Handle group by change
    const handleGroupByChange = (option) => {
        setFilters(prev => ({ ...prev, groupBy: option?.value || 'daily' }));
        setPagination(prev => ({ ...prev, page: 1 }));
        setTimeout(() => loadReportData(1), 0);
    };

    // Handle page change
    const handlePageChange = (page) => {
        setPagination(prev => ({ ...prev, page }));
        loadReportData(page);
    };

    // Tooltip renderers
    const renderTooltip = (props, text) => (
        <Tooltip id={`tooltip-${text}`} {...props}>{text}</Tooltip>
    );

    // Table columns based on report type
    const getColumns = () => {
        const baseColumns = [
            {
                title: "PO No",
                dataIndex: "reference_no",
                sorter: true,
                render: (text, record) => (
                    <div>
                        <strong>{text}</strong>
                        <br />
                        <small className="text-muted">{dayjs(record.purchase_date).format('DD/MM/YYYY')}</small>
                    </div>
                ),
            },
            {
                title: "Supplier",
                dataIndex: "supplier_name",
                sorter: true,
                render: (text, record) => (
                    <div>
                        <div>{text}</div>
                        <small className="text-muted">{record.supplier_phone || ''}</small>
                    </div>
                ),
            }
        ];

        switch (reportType) {
            case 'detailed':
                return [
                    ...baseColumns,
                    {
                        title: "Subtotal",
                        dataIndex: "subtotal",
                        sorter: true,
                        render: (text) => formatCurrency(text),
                    },
                    {
                        title: "Tax",
                        dataIndex: "tax",
                        sorter: true,
                        render: (text) => formatCurrency(text),
                    },
                    {
                        title: "Discount",
                        dataIndex: "discount",
                        sorter: true,
                        render: (text) => formatCurrency(text),
                    },
                    {
                        title: "Shipping",
                        dataIndex: "shipping",
                        sorter: true,
                        render: (text) => formatCurrency(text),
                    },
                    {
                        title: "Grand Total",
                        dataIndex: "grand_total",
                        sorter: true,
                        render: (text) => <strong className="text-primary">{formatCurrency(text)}</strong>,
                    },
                    {
                        title: "Paid",
                        dataIndex: "paid_amount",
                        sorter: true,
                        render: (text) => <span className="text-success">{formatCurrency(text)}</span>,
                    },
                    {
                        title: "Due",
                        dataIndex: "due_amount",
                        sorter: true,
                        render: (text) => (
                            <span className={text > 0 ? 'text-danger' : 'text-success'}>
                                {formatCurrency(text)}
                            </span>
                        ),
                    },
                    {
                        title: "Status",
                        dataIndex: "status",
                        sorter: true,
                        render: (text) => getStatusBadge(text),
                    },
                    {
                        title: "Payment",
                        dataIndex: "payment_status",
                        sorter: true,
                        render: (text) => getPaymentStatusBadge(text),
                    },
                ];

            case 'payment':
                return [
                    ...baseColumns,
                    {
                        title: "Grand Total",
                        dataIndex: "grand_total",
                        sorter: true,
                        render: (text) => formatCurrency(text),
                    },
                    {
                        title: "Paid Amount",
                        dataIndex: "paid_amount",
                        sorter: true,
                        render: (text) => <span className="text-success">{formatCurrency(text)}</span>,
                    },
                    {
                        title: "Due Amount",
                        dataIndex: "due_amount",
                        sorter: true,
                        render: (text) => (
                            <span className={text > 0 ? 'text-danger' : 'text-success'}>
                                {formatCurrency(text)}
                            </span>
                        ),
                    },
                    {
                        title: "Payment Status",
                        dataIndex: "payment_status",
                        sorter: true,
                        render: (text) => getPaymentStatusBadge(text),
                    },
                    {
                        title: "Payments",
                        dataIndex: "payment_count",
                        sorter: true,
                        render: (text) => (
                            <span className="badge bg-info">{text || 0}</span>
                        ),
                    },
                    {
                        title: "Last Payment",
                        dataIndex: "last_payment_date",
                        render: (text, record) => (
                            <div>
                                {text ? dayjs(text).format('DD/MM/YYYY') : 'N/A'}
                                {record.last_payment_method && (
                                    <small className="d-block text-muted">
                                        via {record.last_payment_method}
                                    </small>
                                )}
                            </div>
                        ),
                    },
                ];

            case 'tax':
                return [
                    ...baseColumns,
                    {
                        title: "Taxable Amount",
                        dataIndex: "subtotal",
                        sorter: true,
                        render: (text) => formatCurrency(text),
                    },
                    {
                        title: "Tax Amount",
                        dataIndex: "tax",
                        sorter: true,
                        render: (text) => <span className="text-warning">{formatCurrency(text)}</span>,
                    },
                    {
                        title: "Tax %",
                        render: (text, record) => {
                            const taxPercent = record.subtotal > 0
                                ? ((record.tax / record.subtotal) * 100).toFixed(2)
                                : 0;
                            return <span>{taxPercent}%</span>;
                        },
                    },
                    {
                        title: "Grand Total",
                        dataIndex: "grand_total",
                        sorter: true,
                        render: (text) => formatCurrency(text),
                    },
                ];

            case 'supplier':
                return [
                    {
                        title: "Supplier",
                        dataIndex: "supplier_name",
                        sorter: true,
                        render: (text, record) => (
                            <div>
                                <strong>{text}</strong>
                                <br />
                                <small className="text-muted">{record.supplier_phone || ''}</small>
                            </div>
                        ),
                    },
                    {
                        title: "Total Orders",
                        render: (text, record) => {
                            const count = purchases.filter(p => p.supplier_id === record.supplier_id).length;
                            return <span className="badge bg-primary">{count}</span>;
                        },
                    },
                    {
                        title: "Total Amount",
                        render: (text, record) => {
                            const total = purchases
                                .filter(p => p.supplier_id === record.supplier_id)
                                .reduce((sum, p) => sum + (p.grand_total || 0), 0);
                            return formatCurrency(total);
                        },
                    },
                    {
                        title: "Total Paid",
                        render: (text, record) => {
                            const total = purchases
                                .filter(p => p.supplier_id === record.supplier_id)
                                .reduce((sum, p) => sum + (p.paid_amount || 0), 0);
                            return <span className="text-success">{formatCurrency(total)}</span>;
                        },
                    },
                    {
                        title: "Total Due",
                        render: (text, record) => {
                            const total = purchases
                                .filter(p => p.supplier_id === record.supplier_id)
                                .reduce((sum, p) => sum + (p.due_amount || 0), 0);
                            return (
                                <span className={total > 0 ? 'text-danger' : 'text-success'}>
                                    {formatCurrency(total)}
                                </span>
                            );
                        },
                    },
                ];

            case 'item':
                return [
                    {
                        title: "Product",
                        dataIndex: "product_name",
                        render: (text, record) => (
                            <div>
                                <strong>{record.product_name}</strong>
                                <br />
                                <small className="text-muted">SKU: {record.sku || ''}</small>
                            </div>
                        ),
                    },
                    {
                        title: "Total Qty",
                        dataIndex: "total_qty",
                        sorter: true,
                        render: (text) => <span className="badge bg-info">{text || 0}</span>,
                    },
                    {
                        title: "Total Cost",
                        render: (text, record) => {
                            return formatCurrency(record.total_cost || 0);
                        },
                    },
                    {
                        title: "Avg. Price",
                        render: (text, record) => {
                            const avgPrice = record.total_qty > 0
                                ? (record.total_cost / record.total_qty)
                                : 0;
                            return formatCurrency(avgPrice);
                        },
                    },
                ];

            default: // summary report
                return [
                    ...baseColumns,
                    {
                        title: "Amount",
                        dataIndex: "grand_total",
                        sorter: true,
                        render: (text) => formatCurrency(text),
                    },
                    {
                        title: "Paid",
                        dataIndex: "paid_amount",
                        sorter: true,
                        render: (text) => <span className="text-success">{formatCurrency(text)}</span>,
                    },
                    {
                        title: "Due",
                        dataIndex: "due_amount",
                        sorter: true,
                        render: (text) => (
                            <span className={text > 0 ? 'text-danger' : 'text-success'}>
                                {formatCurrency(text)}
                            </span>
                        ),
                    },
                    {
                        title: "Status",
                        dataIndex: "status",
                        sorter: true,
                        render: (text) => getStatusBadge(text),
                    },
                    {
                        title: "Payment",
                        dataIndex: "payment_status",
                        sorter: true,
                        render: (text) => getPaymentStatusBadge(text),
                    },
                    {
                        title: "Items/Qty",
                        render: (text, record) => (
                            <div>
                                <span className="badge bg-info me-1">Items: {record.items_count || 0}</span>
                                <span className="badge bg-secondary">Qty: {record.total_qty || 0}</span>
                            </div>
                        ),
                    },
                ];
        }
    };

    // Get status badge
    const getStatusBadge = (status) => {
        const badges = {
            'pending': { class: 'badge bg-warning', icon: <Clock size={12} /> },
            'approved': { class: 'badge bg-info', icon: <CheckCircle size={12} /> },
            'received': { class: 'badge bg-success', icon: <Package size={12} /> },
            'cancelled': { class: 'badge bg-danger', icon: <XCircle size={12} /> }
        };
        const badge = badges[status] || { class: 'badge bg-secondary', icon: null };
        return (
            <span className={badge.class}>
                {badge.icon} {status}
            </span>
        );
    };

    // Get payment status badge
    const getPaymentStatusBadge = (status) => {
        const badges = {
            'paid': { class: 'badge bg-success', icon: <CheckCircle size={12} /> },
            'partial': { class: 'badge bg-warning', icon: <AlertTriangle size={12} /> },
            'unpaid': { class: 'badge bg-danger', icon: <XCircle size={12} /> }
        };
        const badge = badges[status] || { class: 'badge bg-secondary', icon: null };
        return (
            <span className={badge.class}>
                {badge.icon} {status}
            </span>
        );
    };

    // Format currency
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 2
        }).format(amount || 0);
    };

    return (
        <div className="page-wrapper">
            <div className="content">
                {/* Header */}
                <div className="page-header">
                    <div className="add-item d-flex">
                        <div className="page-title">
                            <h4>Purchase Invoice Report</h4>
                            <h6>Generate and analyze purchase invoice reports</h6>
                        </div>
                    </div>

                    <ul className="table-top-head">

                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Refresh')}>
                                <button className="btn btn-outline-secondary" onClick={() => loadReportData()}>
                                    <RotateCcw size={18} />
                                </button>
                            </OverlayTrigger>
                        </li>
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Collapse')}>
                                <button
                                    className={data ? "btn btn-outline-secondary active" : "btn btn-outline-secondary"}
                                    onClick={() => dispatch(setToogleHeader(!data))}
                                >
                                    <ChevronUp size={18} />
                                </button>
                            </OverlayTrigger>
                        </li>
                    </ul>
                </div>

                {/* Summary Cards */}
                <div className="row mb-4">
                    <div className="col-xl-2 col-sm-4 col-6">
                        <div className="card">
                            <div className="card-body">
                                <h5 className="card-title text-muted">Total Orders</h5>
                                <h3 className="mb-0 text-primary">{summary.totalPurchases}</h3>
                            </div>
                        </div>
                    </div>
                    <div className="col-xl-2 col-sm-4 col-6">
                        <div className="card">
                            <div className="card-body">
                                <h5 className="card-title text-muted">Total Amount</h5>
                                <h3 className="mb-0 text-primary">{formatCurrency(summary.totalAmount)}</h3>
                            </div>
                        </div>
                    </div>
                    <div className="col-xl-2 col-sm-4 col-6">
                        <div className="card">
                            <div className="card-body">
                                <h5 className="card-title text-muted">Total Paid</h5>
                                <h3 className="mb-0 text-success">{formatCurrency(summary.totalPaid)}</h3>
                            </div>
                        </div>
                    </div>
                    <div className="col-xl-2 col-sm-4 col-6">
                        <div className="card">
                            <div className="card-body">
                                <h5 className="card-title text-muted">Total Due</h5>
                                <h3 className="mb-0 text-danger">{formatCurrency(summary.totalDue)}</h3>
                            </div>
                        </div>
                    </div>
                    <div className="col-xl-2 col-sm-4 col-6">
                        <div className="card">
                            <div className="card-body">
                                <h5 className="card-title text-muted">Total Tax</h5>
                                <h3 className="mb-0 text-warning">{formatCurrency(summary.totalTax)}</h3>
                            </div>
                        </div>
                    </div>
                    <div className="col-xl-2 col-sm-4 col-6">
                        <div className="card">
                            <div className="card-body">
                                <h5 className="card-title text-muted">Avg. Order</h5>
                                <h3 className="mb-0 text-info">{formatCurrency(summary.averagePurchase)}</h3>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Status Summary Cards */}
                <div className="row mb-4">
                    <div className="col-xl-3 col-sm-6 col-12">
                        <div className="card bg-success bg-opacity-10">
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <h6 className="text-muted mb-1">Received</h6>
                                        <h3 className="mb-0 text-success">{summary.receivedCount}</h3>
                                    </div>
                                    <div className="bg-success rounded-circle p-3">
                                        <Package size={24} className="text-white" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-xl-3 col-sm-6 col-12">
                        <div className="card bg-warning bg-opacity-10">
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <h6 className="text-muted mb-1">Pending</h6>
                                        <h3 className="mb-0 text-warning">{summary.pendingCount}</h3>
                                    </div>
                                    <div className="bg-warning rounded-circle p-3">
                                        <Clock size={24} className="text-white" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-xl-3 col-sm-6 col-12">
                        <div className="card bg-info bg-opacity-10">
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <h6 className="text-muted mb-1">Paid</h6>
                                        <h3 className="mb-0 text-info">{summary.paidCount}</h3>
                                    </div>
                                    <div className="bg-info rounded-circle p-3">
                                        <DollarSign size={24} className="text-white" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-xl-3 col-sm-6 col-12">
                        <div className="card bg-danger bg-opacity-10">
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <h6 className="text-muted mb-1">Partial</h6>
                                        <h3 className="mb-0 text-danger">{summary.partialCount}</h3>
                                    </div>
                                    <div className="bg-danger rounded-circle p-3">
                                        <AlertTriangle size={24} className="text-white" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Filters Card */}
                <div className="card table-list-card">
                    <div className="card-body">
                        <div className="table-top">
                            <div className="search-set">
                                <div className="search-input">
                                    <input
                                        type="text"
                                        placeholder="Search by PO, supplier..."
                                        className="form-control form-control-sm formsearch"
                                        value={filters.search}
                                        onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                                        onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                    />
                                    <button className="btn btn-searchset" onClick={handleSearch}>
                                        <Search size={16} />
                                    </button>
                                </div>
                            </div>

                            <div className="search-path">
                                <div className="d-flex align-items-center">
                                    <button
                                        className={`btn btn-filter ${isFilterVisible ? "setclose" : ""}`}
                                        onClick={toggleFilterVisibility}
                                    >
                                        <Filter size={18} />
                                    </button>
                                </div>
                            </div>

                            <div className="form-sort">
                                <Sliders size={16} className="info-img me-2" />
                                <Select
                                    className="select"
                                    options={reportTypeOptions}
                                    value={reportTypeOptions.find(opt => opt.value === reportType)}
                                    onChange={handleReportTypeChange}
                                    placeholder="Report Type"
                                    isClearable={false}
                                />
                            </div>
                        </div>

                        {/* Filter Panel */}
                        <div
                            className={`card${isFilterVisible ? ' visible' : ''}`}
                            id="filter_inputs"
                            style={{ display: isFilterVisible ? 'block' : 'none' }}
                        >
                            <div className="card-body pb-0">
                                <div className="row">
                                    <div className="col-lg-3 col-sm-6 col-12">
                                        <div className="input-blocks">
                                            <User size={16} className="info-img" />
                                            <Select
                                                options={suppliers}
                                                value={suppliers.find(s => s.value === filters.supplier_id)}
                                                onChange={(opt) => handleFilterChange('supplier_id', opt?.value || 0)}
                                                placeholder="All Suppliers"
                                                isClearable
                                            />
                                        </div>
                                    </div>
                                    <div className="col-lg-3 col-sm-6 col-12">
                                        <div className="input-blocks">
                                            <StopCircle size={16} className="info-img" />
                                            <Select
                                                options={statusFilterOptions}
                                                value={statusFilterOptions.find(s => s.value === filters.status)}
                                                onChange={(opt) => handleFilterChange('status', opt?.value || '')}
                                                placeholder="All Status"
                                                isClearable
                                            />
                                        </div>
                                    </div>
                                    <div className="col-lg-3 col-sm-6 col-12">
                                        <div className="input-blocks">
                                            <CreditCard size={16} className="info-img" />
                                            <Select
                                                options={paymentStatusFilterOptions}
                                                value={paymentStatusFilterOptions.find(p => p.value === filters.payment_status)}
                                                onChange={(opt) => handleFilterChange('payment_status', opt?.value || '')}
                                                placeholder="All Payment"
                                                isClearable
                                            />
                                        </div>
                                    </div>
                                    <div className="col-lg-3 col-sm-6 col-12">
                                        <div className="input-blocks">
                                            <PieChart size={16} className="info-img" />
                                            <Select
                                                options={groupByOptions}
                                                value={groupByOptions.find(g => g.value === filters.groupBy)}
                                                onChange={handleGroupByChange}
                                                placeholder="Group By"
                                                isClearable={false}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-lg-6 col-sm-12 col-12">
                                        <div className="input-blocks">
                                            <div className="position-relative daterange-wraper">
                                                <Calendar size={16} className="info-img" />
                                                <RangePicker
                                                    value={[filters.startDate, filters.endDate]}
                                                    onChange={handleDateRangeChange}
                                                    format="DD/MM/YYYY"
                                                    placeholder={['From Date', 'To Date']}
                                                    style={{ width: '100%' }}
                                                    ranges={datePresets}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-lg-3 col-sm-6 col-12">
                                        <div className="input-blocks">
                                            <button className="btn btn-primary w-100" onClick={() => loadReportData(1)}>
                                                <Search size={16} className="me-2" />
                                                Generate Report
                                            </button>
                                        </div>
                                    </div>
                                    <div className="col-lg-3 col-sm-6 col-12">
                                        <div className="input-blocks">
                                            <button className="btn btn-secondary w-100" onClick={resetFilters}>
                                                <RotateCcw size={16} className="me-2" />
                                                Reset
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Report Table */}
                        <div className="table-responsive">
                            <Table
                                columns={getColumns()}
                                dataSource={purchases}
                                loading={loading}
                                pagination={{
                                    current: pagination.page,
                                    pageSize: pagination.limit,
                                    total: pagination.total,
                                    onChange: handlePageChange,
                                    showSizeChanger: true,
                                    showQuickJumper: true,
                                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} orders`
                                }}
                            />
                        </div>

                        {/* Report Footer with Totals */}
                        {purchases.length > 0 && (
                            <div className="row mt-3">
                                <div className="col-md-12">
                                    <div className="card bg-light">
                                        <div className="card-body">
                                            <div className="row">
                                                <div className="col-md-2">
                                                    <p className="mb-1 text-muted">Total Records</p>
                                                    <h6 className="fw-bold">{pagination.total}</h6>
                                                </div>
                                                <div className="col-md-2">
                                                    <p className="mb-1 text-muted">Total Amount</p>
                                                    <h6 className="fw-bold text-primary">
                                                        {formatCurrency(purchases.reduce((sum, p) => sum + (p.grand_total || 0), 0))}
                                                    </h6>
                                                </div>
                                                <div className="col-md-2">
                                                    <p className="mb-1 text-muted">Total Paid</p>
                                                    <h6 className="fw-bold text-success">
                                                        {formatCurrency(purchases.reduce((sum, p) => sum + (p.paid_amount || 0), 0))}
                                                    </h6>
                                                </div>
                                                <div className="col-md-2">
                                                    <p className="mb-1 text-muted">Total Due</p>
                                                    <h6 className="fw-bold text-danger">
                                                        {formatCurrency(purchases.reduce((sum, p) => sum + (p.due_amount || 0), 0))}
                                                    </h6>
                                                </div>
                                                <div className="col-md-2">
                                                    <p className="mb-1 text-muted">Total Tax</p>
                                                    <h6 className="fw-bold text-warning">
                                                        {formatCurrency(purchases.reduce((sum, p) => sum + (p.tax || 0), 0))}
                                                    </h6>
                                                </div>
                                                <div className="col-md-2">
                                                    <p className="mb-1 text-muted">Avg. Order</p>
                                                    <h6 className="fw-bold text-info">
                                                        {formatCurrency(
                                                            purchases.reduce((sum, p) => sum + (p.grand_total || 0), 0) / purchases.length
                                                        )}
                                                    </h6>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PurchaseInvoiceReport;